import { useMatch } from "react-router-dom";
import { Show, useModal } from "@refinedev/antd";
import { AppTitle } from "../../../components";
import { leagueIndex } from "../Lib";
import { useOne, useList } from "@refinedev/core";
import { errorNotification } from "../../../helpers";
import {
  Button,
  Space,
  Typography,
  Modal,
  Input,
  Table,
  Tag,
  message,
} from "antd";
import { Match } from "../Lib/types";
import { MatchesTeams } from "./components/Matches-Teams";
import { MatchesMainInfo } from "./components/Matches-Main-Info";
import { MatchesTranslation } from "./components/Matches-Translation";
import { MatchesMedia } from "./components/Matches-Media";
import { MatchesDescription } from "./components/Matches-Description";
import { MatchesAnnounce } from "./components/Matches-Announce";
import { MatchesAnnounceConf } from "./components/Matches-Announce-Conf";
import { useState, useEffect } from "react";
import axAPI from "../../../api/axios-instance";

const { Title, Text } = Typography;

interface MatchCommentator {
  id: number;
  commentatorId: number;
  leagueId: number;
  matchId: number;
}

interface Commentator {
  id: number;
  firstName: string;
  lastName: string;
  patronymic?: string;
  city: string;
  category: string;
  photo?: string;
  birthdate?: string;
  voice?: string;
}

export const MatchesShow = () => {
  const route = useMatch("/:parent/:league/:season/:seasonId/:show/:matchId");
  const { modalProps, show, close } = useModal();

  const [searchQuery, setSearchQuery] = useState("");
  const [commentators, setCommentators] = useState<Commentator[]>([]);
  const [filteredCommentators, setFilteredCommentators] = useState<
    Commentator[]
  >([]);
  const [matchCommentators, setMatchCommentators] = useState<
    MatchCommentator[]
  >([]);
  const [loadingCommentators, setLoadingCommentators] = useState<number[]>([]);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const matchId = route?.params.matchId;
  const leagueId = leagueIndex[`${route?.params.league}`];

  // Загрузка всех комментаторов
  const { data: commentatorsData, isLoading: commentatorsLoading } =
    useList<Commentator>({
      resource: "commentators",
      dataProviderName: "commentatorsDataProvider",
    });

  const { data, isLoading, refetch } = useOne<Match>({
    resource: "matches",
    id: "",
    meta: {
      matchId,
      leagueId,
    },
    queryOptions: {
      enabled: !!(matchId && leagueId),
    },
    dataProviderName: "matchesDataProvider",
    ...errorNotification(),
  });

  // Загрузка комментаторов матча
  const loadMatchCommentators = async () => {
    if (!matchId) return;

    try {
      const response = await axAPI.get("cms/commentators/getMatch", {
        params: { matchId: parseInt(matchId) },
      });
      setMatchCommentators(response.data || []);
    } catch (error) {
      console.error("Error loading match commentators:", error);
    }
  };

  useEffect(() => {
    if (commentatorsData?.data) {
      setCommentators(commentatorsData.data);
      setFilteredCommentators(commentatorsData.data);
    }
  }, [commentatorsData]);

  useEffect(() => {
    if (matchId) {
      loadMatchCommentators();
    }
  }, [matchId]);

  // Фильтрация комментаторов по ФИО
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredCommentators(commentators);
    } else {
      const filtered = commentators.filter((commentator) =>
        `${commentator.lastName} ${commentator.firstName} ${
          commentator.patronymic || ""
        }`
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      );
      setFilteredCommentators(filtered);
    }
  }, [searchQuery, commentators]);

  const handleRefetchCard = () => {
    refetch();
  };

  // Добавление комментатора к матчу
  const handleAddCommentator = async (commentatorId: number) => {
    if (!matchId || !leagueId) return;

    setLoadingCommentators((prev) => [...prev, commentatorId]);

    try {
      await axAPI.post("cms/commentators/addMatch", {
        commentatorId,
        matchId: parseInt(matchId),
        leagueId: parseInt(leagueId),
      });

      message.success("Комментатор успешно добавлен");
      await loadMatchCommentators();
      close();
    } catch (error) {
      console.error("Error adding commentator:", error);
      message.error("Ошибка при добавлении комментатора");
    } finally {
      setLoadingCommentators((prev) =>
        prev.filter((id) => id !== commentatorId)
      );
    }
  };

  // Удаление комментатора из матча
  const handleRemoveCommentator = async (rowId: number) => {
    setDeleteLoading(true);
    try {
      await axAPI.get("cms/commentators/removeMatch", {
        params: { rowId },
      });

      message.success("Комментатор успешно удален");
      await loadMatchCommentators();
    } catch (error) {
      console.error("Error removing commentator:", error);
      message.error("Ошибка при удалении комментатора");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Проверка, загружается ли конкретный комментатор
  const isLoadingCommentator = (commentatorId: number) => {
    return loadingCommentators.includes(commentatorId);
  };

  const record = data?.data as Match;

  return (
    <Show
      isLoading={isLoading}
      headerButtons={<></>}
      title={
        <AppTitle canGoBack isLoading={isLoading}>
          {`Матч – «${record?.home?.name} / ${record?.guest?.name}»`}
        </AppTitle>
      }
    >
      <Space direction={"vertical"} size={32} style={{ width: "100%" }}>
        <MatchesMainInfo
          date={record?.date}
          stadium={record?.stadium}
          tour={record?.tour}
        />
        <MatchesDescription
          matchId={record?.matchId}
          leagueId={record?.leagueId}
          descriptions={record?.descriptions}
          onRefetchCard={handleRefetchCard}
        />

        {/* Секция комментаторов матча */}
        <div>
          <Title level={5}>Комментаторы матча</Title>
          <Space
            direction="vertical"
            style={{ width: "100%", marginTop: "20px" }}
            size={8}
          >
            {matchCommentators.length > 0 ? (
              matchCommentators.map((item) => {
                const commentator = commentators.find(
                  (c) => c.id === item.commentatorId
                );
                return (
                  <div
                    key={item.id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "8px 12px",
                      border: "1px solid #d9d9d9",
                      borderRadius: 6,
                      background: "#fafafa",
                    }}
                  >
                    <Text strong>
                      {commentator
                        ? `${commentator.lastName} ${commentator.firstName} ${
                            commentator.patronymic || ""
                          }`.trim()
                        : `Комментатор ID: ${item.commentatorId}`}
                    </Text>
                    <Button
                      type="text"
                      danger
                      size="small"
                      onClick={() => handleRemoveCommentator(item.id)}
                      loading={deleteLoading}
                      style={{ marginLeft: 8 }}
                    >
                      ×
                    </Button>
                  </div>
                );
              })
            ) : (
              <Text type="secondary">Комментаторы не назначены</Text>
            )}
          </Space>

          <Button type={"primary"} style={{ marginTop: 16 }} onClick={show}>
            Добавить комментатора
          </Button>
        </div>

        <MatchesTeams guest={record?.guest} home={record?.home} />
        <MatchesTranslation
          onRefetchCard={handleRefetchCard}
          matchId={record?.matchId}
          leagueId={record?.leagueId}
          urls={record?.urls}
          fnlId={record?.fnlId}
        />
        <MatchesMedia
          {...record}
          fnlId={record?.fnlId}
          onRefetchCard={handleRefetchCard}
        />
        <MatchesAnnounce {...record} onRefetchCard={handleRefetchCard} />
        <MatchesAnnounceConf {...record} onRefetchCard={handleRefetchCard} />
      </Space>

      {/* Модалка добавления комментатора */}
      <Modal
        {...modalProps}
        title="Добавление комментатора"
        onCancel={close}
        footer={null}
        width={700}
      >
        <Space direction="vertical" style={{ width: "100%" }} size={16}>
          <Input
            placeholder="Поиск по ФИО комментатора..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            allowClear
            size="large"
          />

          <Table<Commentator>
            dataSource={filteredCommentators}
            rowKey="id"
            loading={commentatorsLoading}
            pagination={false}
            scroll={{ y: 400 }}
            size="middle"
          >
            <Table.Column<Commentator>
              title="ФИО"
              key="fullName"
              render={(_, record) => (
                <Text strong>
                  {`${record.lastName} ${record.firstName} ${
                    record.patronymic || ""
                  }`.trim()}
                </Text>
              )}
            />
            <Table.Column<Commentator>
              title="Город"
              dataIndex="city"
              key="city"
              render={(city) => <Text type="secondary">{city}</Text>}
            />
            <Table.Column<Commentator>
              title="Категория"
              dataIndex="category"
              key="category"
              render={(category) => <Tag color="blue">{category}</Tag>}
            />
            <Table.Column<Commentator>
              title="Действие"
              key="action"
              width={120}
              render={(_, record) => (
                <Button
                  type="primary"
                  size="small"
                  onClick={() => handleAddCommentator(record.id)}
                  loading={isLoadingCommentator(record.id)}
                  disabled={matchCommentators.some(
                    (mc) => mc.commentatorId === record.id
                  )}
                >
                  {matchCommentators.some(
                    (mc) => mc.commentatorId === record.id
                  )
                    ? "Добавлен"
                    : "Добавить"}
                </Button>
              )}
            />
          </Table>
        </Space>
      </Modal>
    </Show>
  );
};
